import React, { Fragment, useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useWallet } from '@solana/wallet-adapter-react';
import { WalletMultiButton } from '@solana/wallet-adapter-react-ui';
import { API_URL } from '../constants';
import { Buffer } from 'buffer';
import uploadService from '../services/uploadService';
import { apiRequest } from '../utils/api';

interface UserProfile {
    id: string;
    email?: string;
    walletAddress?: string;
    username?: string;
    avatar?: string;
    bio?: string;
    role: 'admin' | 'creator' | 'consumer';
    kycStatus?: 'pending' | 'approved' | 'rejected' | 'not_submitted';
    monthlyPrice?: number;
    yearlyPrice?: number;
    paymentWalletAddress?: string;
    subscribersCount?: number;
    totalEarnings?: number;
    createdAt: string;
}

const MyProfile: React.FC = () => {
    const [profile, setProfile] = useState<UserProfile | null>(null);
    const [kycStatus, setKycStatus] = useState<'pending' | 'approved' | 'rejected' | 'not_submitted'>('not_submitted');
    const [isEditing, setIsEditing] = useState(false);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [activeTab, setActiveTab] = useState<'profile' | 'creator' | 'security'>('profile');
    const [emailVerificationSent, setEmailVerificationSent] = useState(false);
    const [verificationCode, setVerificationCode] = useState('');
    const [newEmail, setNewEmail] = useState('');
    const [showEmailVerification, setShowEmailVerification] = useState(false);
    const [selectedProfileImage, setSelectedProfileImage] = useState<File | null>(null);
    const [uploadingImage, setUploadingImage] = useState(false);
    const [fileInputRef, setFileInputRef] = useState<HTMLInputElement | null>(null);
    const { signMessage } = useWallet();


    const [editForm, setEditForm] = useState({
        username: '',
        bio: '',
        monthlyPrice: '',
        yearlyPrice: '',
        email: '',
        role: ''
    });

    const [passwordForm, setPasswordForm] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
    });

    const { user, token, login } = useAuth();
    const { publicKey, connected } = useWallet();

    useEffect(() => {
        fetchProfile();
        fetchKYCStatus();
    }, []);

    const fetchKYCStatus = async () => {
        if (!token) return;

        try {
            const response = await fetch(API_URL + '/api/kyc/my-status', {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.ok) {
                const data = await response.json();
                setKycStatus(data.kycStatus);
            }
        } catch (error) {
            console.error('Error fetching KYC status:', error);
        }
    };

    const fetchProfile = async () => {
        try {
            // Using centralized API handler - will automatically handle "Invalid token" errors
            const data = await apiRequest(API_URL + '/api/users/profile');
            setProfile(data.user);
            setEditForm({
                username: data.user.username || '',
                bio: data.user.bio || '',
                monthlyPrice: data.user.monthlyPrice?.toString() || '',
                yearlyPrice: data.user.yearlyPrice?.toString() || '',
                email: data.user.email || '',
                role: data.user.role || ''
            });
        } catch (error: any) {
            setError(error.message || 'Failed to load profile');
        } finally {
            setLoading(false);
        }
    };

    const handleEditFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setEditForm(prev => ({
            ...prev,
            [e.target.name]: e.target.value
        }));
    };

    const handlePasswordFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setPasswordForm(prev => ({
            ...prev,
            [e.target.name]: e.target.value
        }));
    };

    const handleUpdateProfile = async (e: React.FormEvent) => {
        e.preventDefault();
        setSaving(true);
        setError('');
        setSuccess('');

        try {
            const response = await fetch(API_URL + '/api/users/profile', {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    username: editForm.username || undefined,
                    bio: editForm.bio || undefined
                })
            });

            if (response.ok) {
                const data = await response.json();
                setProfile(data.user);
                login(token!, data.user);
                setSuccess('Profile updated successfully');
                setIsEditing(false);
            } else {
                const data = await response.json();
                setError(data.error || 'Failed to update profile');
            }
        } catch (error) {
            setError('Network error occurred');
        } finally {
            setSaving(false);
        }
    };

    const handleUpdateCreatorSettings = async (e: React.FormEvent) => {
        e.preventDefault();
        if (profile?.role !== 'creator' || kycStatus !== 'approved') {
            setError('Only approved creators can update pricing');
            return;
        }

        setSaving(true);
        setError('');
        setSuccess('');

        if (!signMessage) {
            alert('Wallet does not support message signing!');
            return;
        }

        let base58Signature;

        try {
            const message = new TextEncoder().encode('Hello from Solana!');
            const signed = await signMessage(message);
            base58Signature = Buffer.from(signed).toString('base64');
            // setSignature(base58Signature);
            console.log('Signed message:', base58Signature);
        } catch (err) {
            console.error('Signing error:', err);
        }

        try {
            const response = await fetch(API_URL + '/api/kyc/creator-profile', {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    signature: base58Signature,
                    message: 'Hello from Solana!',
                    monthlyPrice: editForm.monthlyPrice ? parseFloat(editForm.monthlyPrice) : undefined,
                    yearlyPrice: editForm.yearlyPrice ? parseFloat(editForm.yearlyPrice) : undefined,
                    paymentWalletAddress: connected && publicKey ? publicKey.toString() : undefined
                })
            });

            if (response.ok) {
                await fetchProfile();
                setSuccess('Creator settings updated successfully');
            } else {
                const data = await response.json();
                setError(data.error || 'Failed to update creator settings');
            }
        } catch (error) {
            setError('Network error occurred');
        } finally {
            setSaving(false);
        }
    };

    const handleChangePassword = async (e: React.FormEvent) => {
        e.preventDefault();
        if (passwordForm.newPassword !== passwordForm.confirmPassword) {
            setError('New passwords do not match');
            return;
        }

        setSaving(true);
        setError('');
        setSuccess('');

        try {
            const response = await fetch(API_URL + '/api/users/change-password', {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    currentPassword: passwordForm.currentPassword,
                    newPassword: passwordForm.newPassword
                })
            });

            if (response.ok) {
                setSuccess('Password changed successfully');
                setPasswordForm({
                    currentPassword: '',
                    newPassword: '',
                    confirmPassword: ''
                });
            } else {
                const data = await response.json();
                setError(data.error || 'Failed to change password');
            }
        } catch (error) {
            setError('Network error occurred');
        } finally {
            setSaving(false);
        }
    };

    const handleRequestEmailVerification = async () => {
        if (!newEmail.trim()) {
            setError('Please enter a new email address');
            return;
        }

        setSaving(true);
        setError('');
        setSuccess('');

        try {
            const response = await fetch(API_URL + '/api/users/request-email-change', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ newEmail })
            });

            if (response.ok) {
                setEmailVerificationSent(true);
                setShowEmailVerification(true);
                setSuccess('Verification code sent to your new email address');
            } else {
                const data = await response.json();
                setError(data.error || 'Failed to send verification email');
            }
        } catch (error) {
            setError('Network error occurred');
        } finally {
            setSaving(false);
        }
    };

    const handleVerifyEmailChange = async () => {
        if (!verificationCode.trim()) {
            setError('Please enter the verification code');
            return;
        }

        setSaving(true);
        setError('');
        setSuccess('');

        try {
            const response = await fetch(API_URL + '/api/users/verify-email-change', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    newEmail,
                    verificationCode
                })
            });

            if (response.ok) {
                const data = await response.json();
                setProfile(data.user);
                login(token!, data.user);
                setEditForm(prev => ({ ...prev, email: data.user.email }));
                setSuccess('Email updated successfully');
                setShowEmailVerification(false);
                setEmailVerificationSent(false);
                setVerificationCode('');
                setNewEmail('');
            } else {
                const data = await response.json();
                setError(data.error || 'Failed to verify email change');
            }
        } catch (error) {
            setError('Network error occurred');
        } finally {
            setSaving(false);
        }
    };

    const handleRoleChange = async () => {
        if (!editForm.role || editForm.role === profile?.role) {
            setError('Please select a different role');
            return;
        }

        setSaving(true);
        setError('');
        setSuccess('');

        try {
            const response = await fetch(API_URL + '/api/users/change-role', {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ role: editForm.role })
            });

            if (response.ok) {
                const data = await response.json();
                setProfile(data.user);
                login(token!, data.user);
                setSuccess('Account type updated successfully');
            } else {
                const data = await response.json();
                setError(data.error || 'Failed to update account type');
            }
        } catch (error) {
            setError('Network error occurred');
        } finally {
            setSaving(false);
        }
    };

    const handleProfileImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            if (file.type.startsWith('image/')) {
                setSelectedProfileImage(file);
            } else {
                setError('Please select an image file');
            }
        }
    };

    const handleUploadProfileImage = async () => {
        if (!selectedProfileImage || !profile?.walletAddress) {
            setError('Please select an image and ensure wallet is connected');
            return;
        }

        setUploadingImage(true);
        setError('');
        setSuccess('');

        try {
            const uploadResponse = await uploadService.uploadProfileImage(selectedProfileImage, profile.walletAddress);

            // Update profile with new avatar
            const response = await fetch(API_URL + '/api/users/profile', {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    avatar: uploadResponse.file.key
                })
            });

            if (response.ok) {
                const data = await response.json();
                // Add cache-busting parameter to the avatar URL
                if (data.user.avatar) {
                    data.user.avatar = `${data.user.avatar}?t=${Date.now()}`;
                }
                setProfile(data.user);
                login(token!, data.user);
                setSuccess('Profile image updated successfully');
                setSelectedProfileImage(null);
            } else {
                const data = await response.json();
                setError(data.error || 'Failed to update profile image');
            }
        } catch (error: any) {
            setError(error.message || 'Failed to upload profile image');
        } finally {
            setUploadingImage(false);
        }
    };

    const handleUpdateEmail = async () => {
        if (!editForm.email || editForm.email === profile?.email) {
            setError('Please enter a different email address');
            return;
        }

        setSaving(true);
        setError('');
        setSuccess('');

        try {
            const response = await fetch(API_URL + '/api/users/profile', {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    email: editForm.email
                })
            });

            if (response.ok) {
                const data = await response.json();
                setProfile(data.user);
                login(token!, data.user);
                setSuccess('Email updated successfully');
            } else {
                const data = await response.json();
                setError(data.error || 'Failed to update email');
            }
        } catch (error) {
            setError('Network error occurred');
        } finally {
            setSaving(false);
        }
    };

    const handleHeaderImageClick = () => {
        if (fileInputRef) {
            fileInputRef.click();
        }
    };

    const handleHeaderImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            if (file.type.startsWith('image/')) {
                if (!profile?.walletAddress) {
                    setError('Wallet address not found');
                    return;
                }

                setUploadingImage(true);
                setError('');
                setSuccess('');

                try {
                    const uploadResponse = await uploadService.uploadProfileImage(file, profile.walletAddress);

                    // Update profile with new avatar
                    const response = await fetch(API_URL + '/api/users/profile', {
                        method: 'PUT',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${token}`
                        },
                        body: JSON.stringify({
                            avatar: uploadResponse.file.key
                        })
                    });

                    if (response.ok) {
                        const data = await response.json();
                        // Add cache-busting parameter to the avatar URL
                        if (data.user.avatar) {
                            data.user.avatar = `${data.user.avatar}?t=${Date.now()}`;
                        }
                        setProfile(data.user);
                        login(token!, data.user);
                        setSuccess('Profile image updated successfully');
                    } else {
                        const data = await response.json();
                        setError(data.error || 'Failed to update profile image');
                    }
                } catch (error: any) {
                    setError(error.message || 'Failed to upload profile image');
                } finally {
                    setUploadingImage(false);
                    // Reset file input to allow selecting the same file again
                    if (fileInputRef) {
                        fileInputRef.value = '';
                    }
                }
            } else {
                setError('Please select an image file');
            }
        }
        // Reset file input to allow selecting the same file again
        if (fileInputRef) {
            fileInputRef.value = '';
        }
    };

    const getKycStatusBadge = (status?: string) => {
        switch (status) {
            case 'approved':
                return <span className="badge bg-success font-xssss">Verified Creator</span>;
            case 'pending':
                return <span className="badge bg-warning font-xssss">KYC Pending</span>;
            case 'rejected':
                return <span className="badge bg-danger font-xssss">KYC Rejected</span>;
            default:
                return <span className="badge bg-secondary font-xssss">Not Applied</span>;
        }
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    if (loading) {
        return (
            <div className="text-center p-5">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    return (
        <Fragment>
            <div className="container-fluid py-2 py-md-4">
                <div className="row justify-content-center">
                    <div className="col-12 col-lg-10 col-xl-8">
                        {/* Profile Header */}
                        <div className="card shadow-xss w-100 d-block border-0 p-0 mb-3"
                            style={{ background: 'linear-gradient(45deg, #667eea 0%, #764ba2 100%)', height: '200px' }}>
                            <div className="card-body p-4 d-flex align-items-end">
                                <div className="d-flex align-items-end">
                                    <div className="me-3 position-relative">
                                        <img
                                            src={profile?.avatar || '/assets/images/user.png'}
                                            alt="Profile"
                                            className="rounded-circle border border-white"
                                            style={{
                                                width: '100px',
                                                height: '100px',
                                                objectFit: 'cover',
                                                cursor: 'pointer',
                                                transition: 'opacity 0.3s ease'
                                            }}
                                            onClick={handleHeaderImageClick}
                                            onMouseEnter={(e) => e.currentTarget.style.opacity = '0.8'}
                                            onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                                        />
                                        {uploadingImage && (
                                            <div
                                                className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center rounded-circle"
                                                style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
                                            >
                                                <div className="spinner-border spinner-border-sm text-white" role="status">
                                                    <span className="visually-hidden">Uploading...</span>
                                                </div>
                                            </div>
                                        )}
                                        <div
                                            className="position-absolute bottom-0 end-0 bg-white rounded-circle d-flex align-items-center justify-content-center"
                                            style={{ width: '30px', height: '30px', cursor: 'pointer' }}
                                            onClick={handleHeaderImageClick}
                                        >
                                            <i className="feather-camera font-xsss text-grey-700"></i>
                                        </div>
                                    </div>
                                    <div className="text-white">
                                        <h2 className="fw-700 font-lg mb-1">
                                            {profile?.username || profile?.email?.split('@')[0] || 'User'}
                                        </h2>
                                        <p className="font-xsss mb-1 opacity-75">
                                            {profile?.email || profile?.walletAddress}
                                        </p>
                                        <div className="d-flex align-items-center">
                                            <span className="badge bg-white text-dark font-xssss me-2">
                                                {profile?.role?.toUpperCase()}
                                            </span>
                                            {profile?.role === 'creator' && getKycStatusBadge(kycStatus)}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Hidden file input for header image upload */}
                            <input
                                type="file"
                                accept="image/*"
                                onChange={handleHeaderImageChange}
                                ref={setFileInputRef}
                                style={{ display: 'none' }}
                            />
                        </div>

                        {error && (
                            <div className="alert alert-danger font-xss fw-500 mb-3">
                                {error}
                            </div>
                        )}

                        {success && (
                            <div className="alert alert-success font-xss fw-500 mb-3">
                                {success}
                            </div>
                        )}

                        {/* Tabs */}
                        <div className="card shadow-xss w-100 d-block border-0 p-0 mb-3">
                            <div className="card-body p-0">
                                <ul className="nav nav-tabs h55 d-flex product-info-tab border-bottom-0 ps-4" role="tablist">
                                    <li className="active list-inline-item me-5">
                                        <a
                                            className={`fw-700 font-xsss text-grey-500 pt-3 pb-3 ls-1 d-inline-block ${activeTab === 'profile' ? 'active' : ''}`}
                                            onClick={() => setActiveTab('profile')}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            Profile Info
                                        </a>
                                    </li>
                                    {profile?.role === 'creator' && (
                                        <li className="list-inline-item me-5">
                                            <a
                                                className={`fw-700 font-xsss text-grey-500 pt-3 pb-3 ls-1 d-inline-block ${activeTab === 'creator' ? 'active' : ''}`}
                                                onClick={() => setActiveTab('creator')}
                                                style={{ cursor: 'pointer' }}
                                            >
                                                Creator Settings
                                            </a>
                                        </li>
                                    )}
                                    <li className="list-inline-item me-5">
                                        <a
                                            className={`fw-700 font-xsss text-grey-500 pt-3 pb-3 ls-1 d-inline-block ${activeTab === 'security' ? 'active' : ''}`}
                                            onClick={() => setActiveTab('security')}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            Security
                                        </a>
                                    </li>
                                </ul>
                            </div>
                        </div>

                        {/* Profile Info Tab */}
                        {activeTab === 'profile' && (
                            <div className="card shadow-xss w-100 d-block border-0 p-4 mb-3">
                                <div className="card-body p-0">
                                    <div className="d-flex align-items-center justify-content-between mb-4">
                                        <h4 className="fw-700 font-xss text-grey-900">Profile Information</h4>
                                        <button
                                            className="btn bg-current text-white font-xsss fw-600 p-2 rounded-3"
                                            onClick={() => setIsEditing(!isEditing)}
                                        >
                                            {isEditing ? 'Cancel' : 'Edit Profile'}
                                        </button>
                                    </div>

                                    {isEditing ? (
                                        <div>
                                            {/* Profile Image Upload Section */}
                                            <div className="form-group mb-4">
                                                <label className="font-xsss fw-600 text-grey-700">Profile Image</label>
                                                <div className="d-flex align-items-center">
                                                    <img
                                                        src={profile?.avatar || '/assets/images/user.png'}
                                                        alt="Current Profile"
                                                        className="rounded-circle me-3"
                                                        style={{ width: '60px', height: '60px', objectFit: 'cover' }}
                                                    />
                                                    <div className="flex-grow-1">
                                                        <input
                                                            type="file"
                                                            accept="image/*"
                                                            onChange={handleProfileImageChange}
                                                            className="form-control mb-2"
                                                        />
                                                        {selectedProfileImage && (
                                                            <div className="d-flex align-items-center">
                                                                <span className="font-xsss text-grey-700 me-2">
                                                                    Selected: {selectedProfileImage.name}
                                                                </span>
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-sm bg-current text-white"
                                                                    onClick={handleUploadProfileImage}
                                                                    disabled={uploadingImage}
                                                                >
                                                                    {uploadingImage ? 'Uploading...' : 'Upload'}
                                                                </button>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                                <small className="text-grey-500">Upload a new profile image (JPG, PNG)</small>
                                            </div>

                                            <form onSubmit={handleUpdateProfile}>
                                                <div className="row">
                                                    <div className="col-lg-6">
                                                        <div className="form-group mb-3">
                                                            <label className="font-xsss fw-600 text-grey-700">Username</label>
                                                            <input
                                                                type="text"
                                                                name="username"
                                                                className="form-control"
                                                                value={editForm.username}
                                                                onChange={handleEditFormChange}
                                                                placeholder="Enter username"
                                                            />
                                                        </div>
                                                    </div>
                                                    <div className="col-lg-6">
                                                        <div className="form-group mb-3">
                                                            <label className="font-xsss fw-600 text-grey-700">Email</label>
                                                            <div className="d-flex">
                                                                <input
                                                                    type="email"
                                                                    name="email"
                                                                    className="form-control me-2"
                                                                    value={editForm.email}
                                                                    onChange={handleEditFormChange}
                                                                    placeholder="Enter email address"
                                                                />
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-sm bg-current text-white"
                                                                    onClick={handleUpdateEmail}
                                                                    disabled={saving || editForm.email === profile?.email}
                                                                >
                                                                    Update
                                                                </button>
                                                            </div>
                                                            <small className="text-grey-500">Update your email address</small>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="form-group mb-3">
                                                    <label className="font-xsss fw-600 text-grey-700">Bio</label>
                                                    <textarea
                                                        name="bio"
                                                        className="form-control"
                                                        rows={4}
                                                        value={editForm.bio}
                                                        onChange={handleEditFormChange}
                                                        placeholder="Tell us about yourself..."
                                                    />
                                                </div>
                                                <div className="form-group">
                                                    <button
                                                        type="submit"
                                                        className="btn bg-success text-white font-xsss fw-600 p-3 rounded-3 me-2"
                                                        disabled={saving}
                                                    >
                                                        {saving ? 'Saving...' : 'Save Changes'}
                                                    </button>
                                                </div>
                                            </form>
                                        </div>
                                    ) : (
                                        <div className="row">
                                            <div className="col-lg-6">
                                                <div className="mb-3">
                                                    <label className="font-xsss fw-600 text-grey-700">Username</label>
                                                    <p className="font-xsss text-grey-900">
                                                        {profile?.username || 'Not set'}
                                                    </p>
                                                </div>
                                                <div className="mb-3">
                                                    <label className="font-xsss fw-600 text-grey-700">Email</label>
                                                    <p className="font-xsss text-grey-900">
                                                        {profile?.email || 'Not set'}
                                                    </p>
                                                </div>
                                                <div className="mb-3">
                                                    <label className="font-xsss fw-600 text-grey-700">Wallet Address</label>
                                                    <p className="font-xsss text-grey-900 text-break">
                                                        {profile?.walletAddress || 'Not connected'}
                                                    </p>
                                                </div>
                                            </div>
                                            <div className="col-lg-6">
                                                <div className="mb-3">
                                                    <label className="font-xsss fw-600 text-grey-700">Role</label>
                                                    <p className="font-xsss text-grey-900">
                                                        {profile?.role ? profile.role.charAt(0).toUpperCase() + profile.role.slice(1) : 'N/A'}
                                                    </p>
                                                </div>
                                                <div className="mb-3">
                                                    <label className="font-xsss fw-600 text-grey-700">Member Since</label>
                                                    <p className="font-xsss text-grey-900">
                                                        {profile?.createdAt ? formatDate(profile.createdAt) : 'Unknown'}
                                                    </p>
                                                </div>
                                            </div>
                                            <div className="col-12">
                                                <div className="mb-3">
                                                    <label className="font-xsss fw-600 text-grey-700">Bio</label>
                                                    <p className="font-xsss text-grey-900">
                                                        {profile?.bio || 'No bio added yet'}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Creator Settings Tab */}
                        {activeTab === 'creator' && profile?.role === 'creator' && (
                            <div className="card shadow-xss w-100 d-block border-0 p-4 mb-3">
                                <div className="card-body p-0">
                                    <h4 className="fw-700 font-xss text-grey-900 mb-4">Creator Settings</h4>

                                    {kycStatus !== 'approved' ? (
                                        <div className="text-center p-4">
                                            <div className="alert alert-warning">
                                                <i className="feather-alert-triangle me-2"></i>
                                                Your KYC is not approved yet. You need KYC approval to set pricing and receive payments.
                                            </div>
                                        </div>
                                    ) : (
                                        <form onSubmit={handleUpdateCreatorSettings}>
                                            <div className="row">
                                                <div className="col-lg-6">
                                                    <div className="form-group mb-3">
                                                        <label className="font-xsss fw-600 text-grey-700">Monthly Subscription (SOL)</label>
                                                        <input
                                                            type="number"
                                                            name="monthlyPrice"
                                                            step="0.01"
                                                            min="0"
                                                            className="form-control"
                                                            value={editForm.monthlyPrice}
                                                            onChange={handleEditFormChange}
                                                            placeholder="0.00"
                                                        />
                                                    </div>
                                                </div>
                                                <div className="col-lg-6">
                                                    <div className="form-group mb-3">
                                                        <label className="font-xsss fw-600 text-grey-700">Yearly Subscription (SOL)</label>
                                                        <input
                                                            type="number"
                                                            name="yearlyPrice"
                                                            step="0.01"
                                                            min="0"
                                                            className="form-control"
                                                            value={editForm.yearlyPrice}
                                                            onChange={handleEditFormChange}
                                                            placeholder="0.00"
                                                        />
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="form-group mb-3">
                                                <label className="font-xsss fw-600 text-grey-700">Payment Wallet</label>
                                                {/* <div className="d-flex align-items-center">
                                                    <WalletMultiButton />
                                                    {connected && publicKey && (
                                                        <span className="ms-3 font-xsss text-success">
                                                            Connected: {publicKey.toString().substring(0, 10)}...
                                                        </span>
                                                    )}
                                                </div> */}

                                                <div className="d-flex align-items-center">

                                                    {connected && publicKey && (
                                                        <span className="font-xsss text-success">
                                                            {publicKey.toString()}
                                                        </span>
                                                    )}
                                                </div>
                                                <small className="text-grey-500">Connect the wallet where you want to receive payments</small>
                                            </div>

                                            <div className="row mb-3">
                                                <div className="col-lg-4">
                                                    <div className="bg-lightblue p-3 rounded-3">
                                                        <h5 className="fw-700 font-xss text-current mb-1">
                                                            {profile.subscribersCount || 0}
                                                        </h5>
                                                        <p className="font-xssss text-grey-500 mb-0">Subscribers</p>
                                                    </div>
                                                </div>
                                                <div className="col-lg-4">
                                                    <div className="bg-lightgreen p-3 rounded-3">
                                                        <h5 className="fw-700 font-xss text-success mb-1">
                                                            {profile.totalEarnings || 0} SOL
                                                        </h5>
                                                        <p className="font-xssss text-grey-500 mb-0">Total Earnings</p>
                                                    </div>
                                                </div>
                                                <div className="col-lg-4">
                                                    <div className="bg-lightyellow p-3 rounded-3">
                                                        <h5 className="fw-700 font-xss text-warning mb-1">
                                                            {kycStatus === 'approved' ? 'Active' : 'Inactive'}
                                                        </h5>
                                                        <p className="font-xssss text-grey-500 mb-0">Creator Status</p>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="form-group">
                                                <button
                                                    type="submit"
                                                    className="btn bg-current text-white font-xsss fw-600 p-3 rounded-3"
                                                    disabled={saving}
                                                >
                                                    {saving ? 'Saving...' : 'Update Settings'}
                                                </button>
                                            </div>
                                        </form>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Security Tab */}
                        {activeTab === 'security' && (
                            <div className="card shadow-xss w-100 d-block border-0 p-4 mb-3">
                                <div className="card-body p-0">
                                    <h4 className="fw-700 font-xss text-grey-900 mb-4">Security Settings</h4>

                                    {profile?.email && (
                                        <form onSubmit={handleChangePassword}>
                                            <h5 className="fw-600 font-xss text-grey-900 mb-3">Change Password</h5>
                                            <div className="form-group mb-3">
                                                <label className="font-xsss fw-600 text-grey-700">Current Password</label>
                                                <input
                                                    type="password"
                                                    name="currentPassword"
                                                    className="form-control"
                                                    value={passwordForm.currentPassword}
                                                    onChange={handlePasswordFormChange}
                                                    required
                                                />
                                            </div>
                                            <div className="row">
                                                <div className="col-lg-6">
                                                    <div className="form-group mb-3">
                                                        <label className="font-xsss fw-600 text-grey-700">New Password</label>
                                                        <input
                                                            type="password"
                                                            name="newPassword"
                                                            className="form-control"
                                                            value={passwordForm.newPassword}
                                                            onChange={handlePasswordFormChange}
                                                            required
                                                        />
                                                    </div>
                                                </div>
                                                <div className="col-lg-6">
                                                    <div className="form-group mb-3">
                                                        <label className="font-xsss fw-600 text-grey-700">Confirm New Password</label>
                                                        <input
                                                            type="password"
                                                            name="confirmPassword"
                                                            className="form-control"
                                                            value={passwordForm.confirmPassword}
                                                            onChange={handlePasswordFormChange}
                                                            required
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="form-group mb-4">
                                                <button
                                                    type="submit"
                                                    className="btn bg-current text-white font-xsss fw-600 p-3 rounded-3"
                                                    disabled={saving}
                                                >
                                                    {saving ? 'Changing...' : 'Change Password'}
                                                </button>
                                            </div>
                                        </form>
                                    )}

                                    <div className="border-top pt-4">
                                        <h5 className="fw-600 font-xss text-grey-900 mb-3">Connected Accounts</h5>
                                        <div className="d-flex align-items-center justify-content-between p-3 bg-greylight rounded-3">
                                            <div className="d-flex align-items-center">
                                                <i className="feather-mail me-3 text-grey-700"></i>
                                                <div>
                                                    <p className="font-xsss fw-600 text-grey-900 mb-0">Email Account</p>
                                                    <p className="font-xssss text-grey-500 mb-0">
                                                        {profile?.email ? 'Connected' : 'Not connected'}
                                                    </p>
                                                </div>
                                            </div>
                                            <span className={`badge ${profile?.email ? 'bg-success' : 'bg-secondary'} font-xssss`}>
                                                {profile?.email ? 'Active' : 'Inactive'}
                                            </span>
                                        </div>
                                        <div className="d-flex align-items-center justify-content-between p-3 bg-greylight rounded-3 mt-2">
                                            <div className="d-flex align-items-center">
                                                <i className="feather-cpu me-3 text-grey-700"></i>
                                                <div>
                                                    <p className="font-xsss fw-600 text-grey-900 mb-0">Solana Wallet</p>
                                                    <p className="font-xssss text-grey-500 mb-0">
                                                        {profile?.walletAddress ? 'Connected' : 'Not connected'}
                                                    </p>
                                                </div>
                                            </div>
                                            <span className={`badge ${profile?.walletAddress ? 'bg-success' : 'bg-secondary'} font-xssss`}>
                                                {profile?.walletAddress ? 'Active' : 'Inactive'}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </Fragment>
    );
};

export default MyProfile;
